import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Play, ShieldCheck } from 'lucide-react';

/**
 * PrivateVideoEmbed — DuckDuckGo "Duck Player"-style click-to-load
 * YouTube embed.
 *
 * Privacy properties:
 *   1. Until the user clicks play, NO request is sent to youtube.com
 *      from this component. We render a thumbnail fetched from
 *      i.ytimg.com (a CDN host that does not set tracking cookies)
 *      with referrerPolicy="no-referrer".
 *   2. On click, we load the iframe pointed at youtube-nocookie.com
 *      (YouTube's privacy-enhanced domain — defers and limits the
 *      cookies it sets). The iframe also has referrerPolicy="no-referrer"
 *      so YouTube cannot see which AADC page the user came from, and a
 *      `sandbox` attribute that restricts what the iframe can do.
 *   3. We never autoplay. The user makes a deliberate decision to load
 *      and watch.
 *
 * Trade-offs:
 *   - Once the user clicks play, YouTube does receive the IP,
 *     user-agent and viewing session of the user. We disclose this on
 *     /trust. We do NOT pretend we can make YouTube zero-tracking
 *     without a server-side proxy (Invidious / Piped), which is out
 *     of scope.
 *   - "Watch on YouTube" link on the placeholder uses the privacy-
 *     enhanced URL too.
 */

// Pull a YouTube video ID out of any common YouTube URL form, or
// accept an 11-char ID directly.
export function extractYouTubeId(input) {
  if (!input) return null;
  const trimmed = String(input).trim();
  // Already an ID
  if (/^[A-Za-z0-9_-]{11}$/.test(trimmed)) return trimmed;
  try {
    const u = new URL(trimmed);
    if (u.hostname === 'youtu.be') {
      return u.pathname.slice(1).split('/')[0] || null;
    }
    if (u.hostname.endsWith('youtube.com') || u.hostname.endsWith('youtube-nocookie.com')) {
      const v = u.searchParams.get('v');
      if (v) return v;
      // /embed/{id}, /shorts/{id}, /v/{id}
      const m = u.pathname.match(/\/(?:embed|shorts|v)\/([A-Za-z0-9_-]{11})/);
      if (m) return m[1];
    }
  } catch (_) {
    // not a URL — fall through
  }
  return null;
}

export default function PrivateVideoEmbed({ url, title = 'YouTube video' }) {
  const videoId = useMemo(() => extractYouTubeId(url), [url]);
  const [loaded, setLoaded] = useState(false);

  if (!videoId) {
    return (
      <div className="rounded-lg border bg-muted p-3 text-xs text-muted-foreground">
        Unsupported video URL.
      </div>
    );
  }

  if (loaded) {
    const src = `https://www.youtube-nocookie.com/embed/${videoId}?rel=0&autoplay=1&modestbranding=1`;
    return (
      <figure className="space-y-1.5">
        <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-black" data-testid="private-video-iframe-wrap">
          <iframe
            src={src}
            title={title}
            loading="lazy"
            allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            referrerPolicy="no-referrer"
            sandbox="allow-scripts allow-same-origin allow-presentation"
            className="absolute inset-0 w-full h-full border-0"
          />
        </div>
        <PrivateEmbedCaption />
      </figure>
    );
  }

  // Pre-click placeholder — no requests to youtube.com yet
  const thumbnail = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
  return (
    <figure className="space-y-1.5">
      <button
        type="button"
        onClick={() => setLoaded(true)}
        className="relative aspect-video w-full overflow-hidden rounded-lg bg-black group focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-600"
        aria-label={`Play ${title} (loads YouTube only when you click)`}
        data-testid="private-video-placeholder"
      >
        <img
          src={thumbnail}
          alt={title}
          className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
          loading="lazy"
          referrerPolicy="no-referrer"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-black/30" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-16 h-16 rounded-full bg-white/95 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform">
            <Play className="w-7 h-7 text-amber-700 fill-amber-700 ml-1" />
          </div>
        </div>
        <div className="absolute bottom-2 left-2 right-2 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600/90 text-white text-[10px] px-2 py-0.5 font-medium">
            <ShieldCheck className="w-3 h-3" />
            Private mode
          </span>
          <span className="text-[10px] text-white/85 hidden sm:inline">
            Click to load &mdash; no data sent to YouTube until you do
          </span>
        </div>
      </button>
      <PrivateEmbedCaption />
    </figure>
  );
}

/**
 * Small, always-visible caption beneath the video clarifying that this
 * embed uses YouTube's privacy-enhanced domain, blocks referrers, and
 * is sandboxed. Links to /trust for the longer explanation.
 */
function PrivateEmbedCaption() {
  return (
    <figcaption
      className="flex items-center gap-1.5 text-[11px] text-emerald-700"
      data-testid="private-video-caption"
    >
      <ShieldCheck className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
      <span>
        Private embed &middot; youtube-nocookie.com, no referrer.{' '}
        <Link
          to="/trust"
          className="underline decoration-dotted hover:decoration-solid"
        >
          Why?
        </Link>
      </span>
    </figcaption>
  );
}
